import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { AttributePanelState } from '../../../services/animation/animation.types';
import { setDescriptorValue } from '../../store/editor.crud.actions';
import {
  Font,
  FontService,
} from '../../control-panel/canvas-control-panel/text/font.service';
import { CanvasService } from '../../../services/canvas/canvas.service';
import { Store } from '@ngrx/store';
import { ShapeService } from '../../shape/shape.service';
import { selectEditorState } from '../../store/selector/editor.selector';
import { map } from 'rxjs/operators';
import { getCombinedColorPalette } from '../../../projects/project.selector';
import {
  bulkUpdateFinish,
  toggleEditorState,
} from '../../store/editor.actions';
import { addColorPaletteItem } from '../../../projects/project.actions';

@Component({
  selector: 'nw-fill-panel',
  templateUrl: './fill-panel.component.html',
  styleUrls: ['./fill-panel.component.scss'],
})
export class FillPanelComponent implements OnDestroy {
  @ViewChild('search') searchElement: ElementRef;

  fonts$: Observable<Font[]>;
  showSelector = false;
  private searchText$ = new BehaviorSubject<string>('');

  clickSub: Subscription;
  searchText = 'Search';
  selectedFont: string;
  currentColor: string;
  editActive = false;
  colorPalette$: Observable<Array<{ label: string; color: string }>>;
  colorPalette: Record<string, string> = {};
  subCb: Subscription;
  pristineColor: string;
  saveAsVariableMode = false;
  variableIsTaken = false;

  // @Input()
  set color(val: string) {
    this.currentColor = val;
    this.pristineColor = val;
    this.currentColor$.next(val);
  }

  @Input()
  set state(state: AttributePanelState) {
    if (!state?.value) {
      // this.config = {
      //   color: undefined,
      //   width: undefined,
      //   dash: undefined,
      // };
      return;
    }
    // console.log('val', val);
    const val = state.value.color?.[0] as string;
    this.currentColor = val;
    this.pristineColor = val;
    this.currentColor$.next(val);

    // this.config = {
    //   color: val.value.color?.[0],
    //   width: val.value.width?.[0],
    //   dash: val.value.dash?.[0],
    // };
  }

  get resolvedColor() {
    return this.shapeService.resolveColor(this.currentColor);
  }

  get fontFamilyToDisplay() {
    return this.currentColor;
  }

  get isVariableMode() {
    return this.currentColor.startsWith('$');
  }

  get mode() {
    if (this.saveAsVariableMode) {
      if (this.variableIsTaken) {
        return 'variable-is-taken';
      }
      return 'variable-save';
    }

    if (this.currentColor?.startsWith('$')) {
      return 'variable-selected';
    }
    return 'default';
  }
  currentColor$ = new BehaviorSubject<string>('');
  showSelector$: Observable<boolean>;
  constructor(
    private readonly service: FontService,
    private readonly cs: CanvasService,
    private readonly store: Store,
    private readonly shapeService: ShapeService,
  ) {
    // -- //
    this.showSelector$ = this.store
      .select(selectEditorState('panelOpened'))
      .pipe(map(state => state == 'fill'));

    this.colorPalette$ = combineLatest([
      this.store.select(getCombinedColorPalette),
      this.currentColor$,
    ]).pipe(
      map(([colors, currentColor]) =>
        colors.filter(({ label }) => {
          if (currentColor?.startsWith('$')) {
            return this.currentColor.slice(2) !== label;
          }
          return true;
        }),
      ),
    );
    this.subCb = this.colorPalette$.subscribe(
      cb =>
        (this.colorPalette = cb.reduce((object, { label, color }) => {
          object[label] = color;
          return object;
        }, {})),
    );

    this.clickSub = this.cs.clickEvent.subscribe(() => {
      this.showSelector = false;
    });
  }

  get searchTextBaseFormat() {
    return this.searchText.charAt(0).toUpperCase() + this.searchText.slice(1);
  }
  variableName = '';

  saveAsVariable() {
    this.saveAsVariableMode = true;
    const variableName = this.getClosestColor(this.currentColor);
    // -- // -- //
    console.log('variable-name', variableName);
  }

  saveVariable() {
    // -- // -- // -- //
    this.saveAsVariableMode = false;
    // -- // -- // -- // -- //

    this.store.dispatch(
      addColorPaletteItem({
        variableName: '',
        color: this.currentColor,
      }),
    );
  }
  removeFill() {
    this.store.dispatch(
      setDescriptorValue({
        key: 'fill',
        value: undefined,
      }),
    );
    this.showSelector = false;
    this.store.dispatch(bulkUpdateFinish());
  }

  getClosestColor(hex: string) {
    const colors = {
      red: [255, 0, 0],
      green: [0, 255, 0],
      blue: [0, 0, 255],
      gray: [128, 128, 128],
      yellow: [255, 255, 0],
      violet: [138, 43, 226], // Adjusted from purple to violet
      orange: [255, 165, 0],
      brown: [165, 42, 42], // Added to better categorize dark oranges
    };

    function hexToRgb(hex) {
      hex = hex.replace('#', '');
      if (hex.length === 3) {
        hex = hex
          .split('')
          .map(c => c + c)
          .join('');
      }
      const bigint = parseInt(hex, 16);
      return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
    }

    function colorDistance(c1, c2) {
      return Math.sqrt(
        Math.pow(c1[0] - c2[0], 2) +
          Math.pow(c1[1] - c2[1], 2) +
          Math.pow(c1[2] - c2[2], 2),
      );
    }

    function getLightnessCategory(rgb) {
      const brightness =
        (rgb[0] * 0.299 + rgb[1] * 0.587 + rgb[2] * 0.114) / 255;
      if (brightness > 0.75) return 'light';
      if (brightness < 0.4) return 'dark';
      return 'normal';
    }

    function isGrayish(rgb) {
      const [r, g, b] = rgb;
      const maxDiff = 30; // Allowable difference for grayish colors
      return (
        Math.abs(r - g) < maxDiff &&
        Math.abs(g - b) < maxDiff &&
        Math.abs(r - b) < maxDiff
      );
    }

    const inputRgb = hexToRgb(hex);
    let closestColor = null;
    let minDistance = Infinity;
    let secondClosestColor = null;
    let secondMinDistance = Infinity;

    for (const [color, rgb] of Object.entries(colors)) {
      const distance = colorDistance(inputRgb, rgb);
      if (distance < minDistance) {
        secondClosestColor = closestColor;
        secondMinDistance = minDistance;
        minDistance = distance;
        closestColor = color;
      } else if (distance < secondMinDistance) {
        secondMinDistance = distance;
        secondClosestColor = color;
      }
    }

    if (closestColor === 'gray' && !isGrayish(inputRgb)) {
      closestColor = secondClosestColor;
    }

    const category = getLightnessCategory(inputRgb);
    return `${closestColor} (${category})`;
  }
  dropDownClick() {
    this.editActive = false;
    this.updateColor(this.pristineColor);
  }

  updateColor(value: string) {
    this.store.dispatch(
      setDescriptorValue({
        key: 'fill',
        innerKey: 'color',
        value,
        direct: true,
      }),
    );
    // this.finishUpdate();
  }
  ngOnDestroy(): void {
    // this.sub.unsubscribe();
    this.subCb.unsubscribe();
    this.clickSub.unsubscribe();
  }

  finishUpdate() {
    this.store.dispatch(bulkUpdateFinish());
  }

  ngAfterViewInit() {
    this.clickSub = this.cs.clickEvent.subscribe(() => {
      this.showSelector = false;
    });
  }

  close() {}

  toggleSelector() {
    /* if (!this.showSelector && !this.currentColor) {
      this.updateColor('#ffffff');
    } */

    this.store.dispatch(
      toggleEditorState({
        key: 'panelOpened',
        value: 'fill',
      }),
    );

    this.showSelector = !this.showSelector;

    if (!this.showSelector) {
      this.editActive = false;
    }
  }

  searchTextChange(event: Event) {
    event.stopPropagation();
    const value = (event.target as HTMLInputElement).value;

    const searchText = value.charAt(0).toUpperCase() + value.slice(1);
    this.searchText$.next(searchText);
  }

  @HostListener('wheel', ['$event'])
  onWheel(event: WheelEvent) {
    event.stopPropagation();
  }

  selectFont(font: Font) {
    this.updateColor(font.name);
    this.service.loadFont(font, () => {
      this.finishUpdate();
    });
    this.showSelector = false;
  }
}
